import Decimal from 'decimal.js';
import { DECIMAL_CONVENTION, getDecimalConvention } from '../stores/useDecimalConventionStore';
import { unwrapDecimal128, usesCommaDecimal } from './numberFormat';

// Collapse a possibly-AUTO convention to the concrete European/American shape
// its number formatting actually produces. Without this, the new AUTO default
// (a Spanish user who never touched the setting) compares unequal to EUROPEAN
// below and gets American thousands-handling — so "1.234.567" typed by a
// Spanish user would be misread. Derives the answer from the same Intl the
// formatter uses, so input parsing and display can never disagree.
const resolveConvention = (convention) => {
  if (convention === DECIMAL_CONVENTION.EUROPEAN || convention === DECIMAL_CONVENTION.AMERICAN) {
    return convention;
  }
  return usesCommaDecimal(convention) ? DECIMAL_CONVENTION.EUROPEAN : DECIMAL_CONVENTION.AMERICAN;
};

// Normalize a free-form numeric string so it parses cleanly as a Decimal/float.
// Accepts European ("1,5", "1.234,56") and US ("1,234.56") formats.
//
// Strategy:
//   - When both `.` and `,` appear, the rightmost wins as the decimal
//     separator and the other is stripped as a thousands separator. This
//     reliably handles copy-pasted values in either format regardless of
//     the user's chosen convention.
//   - When only one separator type appears:
//       - A single occurrence is treated as a decimal — covers "1,5",
//         "1.5", "0,001".
//       - Multiple occurrences are stripped as thousands ONLY if the
//         separator matches the user's chosen thousands separator
//         (American → ",", European → "."). Otherwise the input is
//         malformed (multiple decimal separators) and we keep only the
//         last as decimal; earlier occurrences are dropped.
// Whitespace is always stripped. Non-string input is returned unchanged.
export const normalizeDecimalInput = (value, rawConvention = getDecimalConvention()) => {
  if (typeof value !== 'string') return value;
  const convention = resolveConvention(rawConvention);

  let v = value.replace(/\s/g, '');
  if (v === '') return v;

  const lastComma = v.lastIndexOf(',');
  const lastDot = v.lastIndexOf('.');

  if (lastComma === -1 && lastDot === -1) return v;

  if (lastComma !== -1 && lastDot !== -1) {
    if (lastComma > lastDot) {
      return v.replace(/\./g, '').replace(',', '.');
    }
    return v.replace(/,/g, '');
  }

  // Only one separator type present.
  const sep = lastComma !== -1 ? ',' : '.';
  const count = sep === ',' ? (v.match(/,/g) || []).length : (v.match(/\./g) || []).length;

  if (count === 1) {
    return sep === ',' ? v.replace(',', '.') : v;
  }

  // Multiple of one type. Strip as thousands only if it matches the user's
  // thousands separator under their convention.
  const thousandsSep = convention === DECIMAL_CONVENTION.EUROPEAN ? '.' : ',';
  if (sep === thousandsSep) {
    return v.split(sep).join('');
  }

  // sep is the user's decimal separator and there are multiple of them —
  // malformed input. Keep only the last as decimal, drop the earlier ones.
  const lastIdx = v.lastIndexOf(sep);
  const before = v.substring(0, lastIdx).split(sep).join('');
  const after = v.substring(lastIdx + 1);
  return `${before}.${after}`;
};

// Construct a Decimal from arbitrary input without throwing. Normalizes
// strings first so European-style commas don't blow up the Decimal parser.
// Returns `fallback` (default null) when the value can't be parsed.
export const safeDecimal = (value, fallback = null) => {
  if (value === null || value === undefined || value === '') return fallback;

  const normalized = normalizeDecimalInput(value);

  try {
    return new Decimal(normalized);
  } catch {
    return fallback;
  }
};

/**
 * A stored decimal as the text an input should hold: plain notation, every
 * place kept. `{ $numberDecimal: "6.54E-7" }` → `"0.000000654"`; a number or a
 * numeric string the same way; `''` for nothing. Something that is not a number
 * comes back as it was, so the field's own validation still sees it.
 */
export const decimalText = (value) => {
  const raw = unwrapDecimal128(value);
  if (raw === null || raw === undefined || raw === '') return '';
  try {
    return new Decimal(raw).toFixed();
  } catch {
    return raw;
  }
};

/**
 * `a − b` for two money figures, to the cent, through Decimal — a figure a
 * reader checks by hand must not carry float drift. A missing value counts
 * as 0.
 */
export const moneyDiff = (a, b) =>
  new Decimal(Number(a) || 0)
    .minus(Number(b) || 0)
    .toDecimalPlaces(2)
    .toNumber();
